import React, { useState, useEffect, useMemo } from "react";
import AppDialog from "@/components/common/AppDialog";
import { SelectField, InputField } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useTran, useFormDirty } from "@/hooks";
import QuestionOptionsEditor from "./QuestionOptionsEditor";
import RequiredToggles from "./RequiredToggles";
import { categories, QUESTION_TYPES } from "../utils/survey-manager.helper";

const MAX_TEXTAREA_ROWS = 10;
const MIN_TEXTAREA_ROWS = 3;
const CHOICE_TYPES = [
  "single_choice",
  "multiple_choice",
  "dropdown",
  "multiple_dropdown",
  "custom_dropdown",
];
const isChoiceType = (type) => CHOICE_TYPES.includes(type);
const isNumericType = (type) => ["number"].includes(type);
const islongText = (type) => ["long_text"].includes(type);
const isScaleType = (type) => type === "scale";
const isOptionEditableType = (type) =>
  type === "single_choice" ||
  type === "multiple_choice" ||
  type === "dropdown" ||
  type === "multiple_dropdown" ||
  type === "custom_dropdown";

const defaultOptionsForType = (type) => {
  if (type === "yes_no") {
    return [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ];
  }

  if (CHOICE_TYPES.includes(type)) {
    return [
      { value: "option1", label: "Option 1" },
      { value: "option2", label: "Option 2" },
    ];
  }
  return [];
};

const CreateQuestionModal = ({ isOpen, onClose, onSubmit, editData }) => {
  const { t } = useTran();
  const categoryOptions = useMemo(() => {
    const items = categories?.length ? categories : ["common"];
    return items.map((category) => ({
      value: category,
      label: category.charAt(0).toUpperCase() + category.slice(1),
    }));
  }, []);

  const questionTypeOptions = useMemo(
    () =>
      QUESTION_TYPES.map((type) => ({
        value: type,
        label: type.replace(/_/g, " "),
      })),
    [],
  );
  const [formData, setFormData] = useState({
    category: "common",
    questionText: "",
    questionType: "text",
    textareaRows: "",
    minValue: "",
    maxValue: "",
    minLabel: "",
    maxLabel: "",
    placeholder: "",
    options: [],
    required: true,
  });
  const [errors, setErrors] = useState({});

  const [dataVersion, setDataVersion] = useState(0);
  const isDirty = useFormDirty(formData, isOpen ? dataVersion : null);

  useEffect(() => {
    if (!isOpen) return;
    if (editData) {
      setFormData({
        category: editData.category || "common",
        questionText: editData.questionText || "",
        questionType: editData.questionType || "text",
        textareaRows: editData.textareaRows || "3",
        minValue: editData.minValue || "",
        maxValue: editData.maxValue || "",
        minLabel: editData.minLabel || "",
        maxLabel: editData.maxLabel || "",
        placeholder: editData.placeholder || "",
        options:
          editData.options || defaultOptionsForType(editData.questionType),
        required: editData.required ?? true,
      });
    } else {
      setFormData({
        category: "common",
        questionText: "",
        questionType: "text",
        textareaRows: 3,
        minValue: "",
        maxValue: "",
        minLabel: "",
        maxLabel: "",
        placeholder: "",
        options: [],
        required: true,
      });
    }
    setDataVersion((v) => v + 1);
  }, [editData, isOpen]);

  const validate = () => {
    const errors = {};

    // Question text
    if (!formData.questionText?.trim()) {
      errors.questionText = "Question text is required";
    }

    // Category
    if (!formData.category) {
      errors.category = "Category is required";
    }

    // Type
    if (!formData.questionType) {
      errors.questionType = "Question type is required";
    }

    // Choice questions must have options
    if (CHOICE_TYPES.includes(formData.questionType)) {
      if (!formData.options || formData.options.length < 2) {
        errors.options = "At least 2 options are required";
      }

      const invalidOption = formData.options?.some(
        (option) => !option.label?.trim() || !option.value?.trim(),
      );

      if (invalidOption) {
        errors.options = "Option label cannot be empty";
      }
    }

    // yes/no validation
    if (formData.questionType === "yes_no" && formData.options.length !== 2) {
      errors.options = "Yes/No question must contain two options";
    }
    // Scale validation
    if (formData.questionType === "scale") {
      const min = Number(formData.minValue);
      const max = Number(formData.maxValue);

      if (formData.minValue === "" || formData.maxValue === "") {
        errors.scale = "Min and Max values are required for scale";
      } else if (isNaN(min) || isNaN(max)) {
        errors.scale = "Min and Max must be valid numbers";
      } else if (min >= max) {
        errors.scale = "Min value must be less than Max value";
      } else if (max - min > 10) {
        errors.scale = "Scale range cannot exceed 10 steps";
      }
    }
    if (formData.questionType === "long_text") {
      const rows = Number(formData.textareaRows);

      if (formData.textareaRows === "" || formData.textareaRows === null) {
        errors.textareaRows = "Textarea rows is required";
      } else if (isNaN(rows)) {
        errors.textareaRows = "Textarea rows must be a valid number";
      } else if (rows < 3) {
        errors.textareaRows = "Textarea rows must be at least 3";
      } else if (rows > 10) {
        errors.textareaRows = "Textarea rows cannot exceed 10";
      }
    }

    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    onSubmit(formData);
  };

  const handleAddOption = () => {
    setFormData((prev) => {
      const options = prev.options || [];
      const nextIndex = options.length + 1;
      return {
        ...prev,
        options: [
          ...options,
          {
            value: `option${nextIndex}`,
            label: `Option ${nextIndex}`,
          },
        ],
      };
    });
  };
  const handleOptionChange = (index, updatedValue) => {
    setFormData((prev) => ({
      ...prev,
      options: prev.options.map((option, i) =>
        i === index
          ? {
              ...option,
              ...updatedValue,
            }
          : option,
      ),
    }));
  };
  const handleRemoveOption = (index) => {
    setFormData((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
    setErrors((prev) => ({ ...prev, options: null }));
  };

  if (!isOpen) return null;

  return (
    <AppDialog
      open={isOpen}
      onOpenChange={(v) => !v && onClose()}
      isDirty={isDirty}
      title={editData ? t("Edit Question") : t("Add Question")}
      description={t(
        "Configure text, category, type, and points. Applied automatically.",
      )}
      size="lg"
      bodyClassName="space-y-4"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button onClick={handleSubmit}>{t("Save Question")}</Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("Question Text")}
          </label>
          <Textarea
            value={formData.questionText}
            onChange={(e) => {
              setFormData({
                ...formData,
                questionText: e.target.value,
              });

              setErrors({
                ...errors,
                questionText: null,
              });
            }}
            placeholder={t("Enter question")}
          />
          {errors.questionText && (
            <p className="text-sm text-red-500">{errors.questionText}</p>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <SelectField
            label={t("Category")}
            labelClassName="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            value={formData.category}
            onChange={(e) =>
              setFormData({ ...formData, category: e.target.value })
            }
            options={categoryOptions}
            placeholder={t("Select category")}
          />
          <SelectField
            label={t("Question Type")}
            labelClassName="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            value={formData.questionType}
            onChange={(e) => {
              const newType = e.target.value;
              const prevType = formData.questionType;
              const bothAreChoiceTypes =
                CHOICE_TYPES.includes(prevType) &&
                CHOICE_TYPES.includes(newType);
              setFormData((prev) => ({
                ...prev,
                questionType: newType,
                options: bothAreChoiceTypes
                  ? prev.options
                  : defaultOptionsForType(newType),
              }));
            }}
            options={questionTypeOptions}
            placeholder={t("Select type")}
          />
        </div>

        {(formData.questionType === "text" ||
          formData.questionType === "long_text") && (
          <InputField
            label={t("Placeholder")}
            labelClassName="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            value={formData.placeholder || ""}
            onChange={(e) =>
              setFormData({ ...formData, placeholder: e.target.value })
            }
            placeholder={t("Enter placeholder")}
          />
        )}
        {isScaleType(formData.questionType) && (
          <div className="space-y-3">
            <div className="grid gap-4 md:grid-cols-2">
              <InputField
                label={t("Min Value")}
                labelClassName="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                type="number"
                value={formData.minValue ?? ""}
                onChange={(e) =>
                  setFormData({ ...formData, minValue: e.target.value })
                }
                placeholder="e.g. 1"
              />
              <InputField
                label={t("Max Value")}
                labelClassName="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                type="number"
                value={formData.maxValue ?? ""}
                onChange={(e) =>
                  setFormData({ ...formData, maxValue: e.target.value })
                }
                placeholder="e.g. 10"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <InputField
                label={t("Min Label")}
                labelClassName="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                value={formData.minLabel || ""}
                onChange={(e) =>
                  setFormData({ ...formData, minLabel: e.target.value })
                }
                placeholder={t("e.g. Just starting")}
              />
              <InputField
                label={t("Max Label")}
                labelClassName="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                value={formData.maxLabel || ""}
                onChange={(e) =>
                  setFormData({ ...formData, maxLabel: e.target.value })
                }
                placeholder={t("e.g. Industry leader")}
              />
            </div>

            {errors.scale && (
              <p className="text-sm text-red-500">{errors.scale}</p>
            )}
          </div>
        )}
        {islongText(formData.questionType) ? (
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <InputField
                label={t("Textarea Rows")}
                labelClassName="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                type="number"
                min={MIN_TEXTAREA_ROWS}
                max={MAX_TEXTAREA_ROWS}
                value={formData.textareaRows ?? ""}
                onChange={(e) => {
                  setFormData({ ...formData, textareaRows: e.target.value });
                  setErrors({ ...errors, textareaRows: null });
                }}
                placeholder={`e.g. 3 (min ${MIN_TEXTAREA_ROWS}, max ${MAX_TEXTAREA_ROWS})`}
              />
              {errors.textareaRows && (
                <p className="text-sm text-red-500">{errors.textareaRows}</p>
              )}
            </div>
          </div>
        ) : null}

        {isNumericType(formData.questionType) ? (
          <div className="grid gap-4 md:grid-cols-2">
            <InputField
              label={t("Minimum")}
              labelClassName="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              type="number"
              value={formData.minValue ?? ""}
              onChange={(e) =>
                setFormData({ ...formData, minValue: e.target.value })
              }
            />
            <InputField
              label={t("Maximum")}
              labelClassName="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              type="number"
              value={formData.maxValue ?? ""}
              onChange={(e) =>
                setFormData({ ...formData, maxValue: e.target.value })
              }
            />
          </div>
        ) : null}
        {isChoiceType(formData.questionType) ? (
          <QuestionOptionsEditor
            options={formData.options}
            questionType={formData.questionType}
            isEditable={isOptionEditableType(formData.questionType)}
            onAddOption={handleAddOption}
            onOptionLabelChange={(index, value) => {
              if (typeof value === "string") {
                handleOptionChange(index, { label: value, value });
              } else {
                handleOptionChange(index, value);
              }
            }}
            onRemoveOption={handleRemoveOption}
          />
        ) : null}
        <RequiredToggles
          required={formData.required}
          onRequiredChange={(checked) =>
            setFormData({ ...formData, required: checked })
          }
        />
      </div>
    </AppDialog>
  );
};

export default CreateQuestionModal;
